import type { ReactNode } from "react";
import { X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { DraftNumberInput } from "@/components/quotes/QuoteLineItemRow";
import type { FormLineItem } from "@/components/quotes/SortableLineItemList";
import { parsePrice, parseQuantity } from "@/components/quotes/lineItemNumbers";
import { cn } from "@/lib/utils";
import { formatMoney } from "./drawerLogic";
import { unitModeOf, type UnitMode } from "./drawerPayload";

interface ItemEditRowProps {
  item: FormLineItem;
  index: number;
  /** SortableLineItemList's drag handle (named "Reorder {name}"). */
  handle: ReactNode;
  tags: string[];
  onChange: (index: number, field: string, value: string | number) => void;
  onRemove: (index: number) => void;
  onUnitMode: (index: number, mode: UnitMode) => void;
}

const UNITS: [UnitMode, string][] = [["guest", "Guest"], ["qty", "Qty"], ["flat", "Flat"]];
const CAPTION: Record<UnitMode, string> = { guest: "per guest", qty: "each", flat: "flat" };

export function DietaryTags({ tags, className }: { tags: string[]; className?: string }) {
  if (tags.length === 0) return null;
  return (
    <>
      {tags.map((t) => (
        <span
          key={t}
          className={cn("ml-[5px] inline-block rounded px-[5px] align-[1px] text-[10.5px] font-bold bg-status-ok-soft text-status-ok", className)}
        >
          {t}
        </span>
      ))}
    </>
  );
}

/** One edit row in the prototype's grid: name, description, price, Guest/Qty/Flat, quantity, total, × (#1559). */
export function ItemEditRow({ item, index, handle, tags, onChange, onRemove, onUnitMode }: ItemEditRowProps) {
  const mode = unitModeOf(item.pricing_type);
  const name = item.name || "line item";
  return (
    <div className="grid grid-cols-[18px_minmax(0,1fr)_92px_112px_84px_32px] items-start gap-2 border-t px-3 py-2.5 first:border-t-0 max-[760px]:grid-cols-[16px_minmax(0,1fr)_minmax(0,1fr)_32px]">
      <div className="pt-1">{handle}</div>
      <div className="grid gap-1.5 max-[760px]:col-span-2">
        <Input
          aria-label="Item name"
          value={item.name}
          onChange={(e) => onChange(index, "name", e.target.value)}
          className="h-8 text-sm"
        />
        <Textarea
          aria-label="Description"
          placeholder="Description, shown to the client"
          value={item.description}
          rows={Math.min(5, Math.max(1, Math.ceil((item.description?.length ?? 0) / 60)))}
          onChange={(e) => onChange(index, "description", e.target.value)}
          className="min-h-[38px] resize-y text-[13px] leading-[1.45]"
        />
        {tags.length > 0 && (
          <div>
            <DietaryTags tags={tags} className="ml-0 mr-[5px]" />
          </div>
        )}
      </div>
      <div className="max-[760px]:col-start-2 max-[760px]:row-start-2">
        <DraftNumberInput
          aria-label="Price"
          inputMode="decimal"
          idleText={item.unit_price}
          parse={parsePrice}
          fallback={0}
          onValue={(n) => onChange(index, "unit_price", n)}
          className="h-8 text-right tabular-nums"
        />
        <div className="mt-0.5 text-center text-[11px] text-muted-foreground">{CAPTION[mode]}</div>
      </div>
      <div className="max-[760px]:col-start-3 max-[760px]:row-start-2">
        <div role="group" aria-label="Priced" className="flex overflow-hidden rounded-[7px] border text-[11.5px]">
          {UNITS.map(([m, label]) => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              onClick={() => onUnitMode(index, m)}
              className={cn("flex-1 px-1 py-1.5", mode === m ? "bg-foreground font-semibold text-background" : "bg-background")}
            >
              {label}
            </button>
          ))}
        </div>
        {mode !== "flat" && (
          <DraftNumberInput
            aria-label="Quantity"
            inputMode="numeric"
            idleText={item.quantity}
            parse={parseQuantity}
            fallback={1}
            onValue={(n) => onChange(index, "quantity", n)}
            className="mt-1.5 h-8 text-right tabular-nums"
          />
        )}
      </div>
      <div className="pt-[7px] text-right font-bold tabular-nums max-[760px]:col-span-2 max-[760px]:col-start-2 max-[760px]:pt-0">
        {formatMoney(item.line_total)}
      </div>
      <button
        type="button"
        aria-label={`Remove ${name}`}
        onClick={() => onRemove(index)}
        className="pt-1 text-[17px] text-muted-foreground hover:text-status-hot max-[760px]:col-start-4 max-[760px]:row-start-1"
      >
        <X className="mx-auto h-4 w-4" />
      </button>
    </div>
  );
}
